import React from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import MyAppText from '../../../components/MyAppText';
import { ScrollView } from 'react-native-web-hover';
import { useForm } from 'react-hook-form';
import MyDateRangePicker from '../../../components/MyDateRangePicker';
import CompanyDropdown from '../../../components/CompanyDropdown';
import { facialService } from '../../../services/central-api/facial';
import CamerasDropdown from '../../../components/facial/CamerasDropdown';
import { MyCheckbox } from '../../../components/MyCheckbox';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
}

type ValuePiece = Date | null;

interface ReportModalForm {
    period: { from: ValuePiece; to: ValuePiece; };
    cameras: string[];
    companyId: number;
    allCameras: boolean;
}

export default function ReportCountModal({ isModalVisible, setModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const defaultEmptyValues: ReportModalForm = {
        period: { from: moment().subtract(1, 'month').toDate(), to: moment().toDate() },
        cameras: [],
        companyId: 0,
        allCameras: false,
    };

    const form = useForm<ReportModalForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    function onCloseNewReportModal() {
        setModalVisible(false);
        form.reset(defaultEmptyValues);
    }

    async function formSubmit() {
        try {
            const values = form.getValues();
            values.period.from!.setHours(0, 0, 0);
            values.period.to!.setHours(0, 0, 0);

            const csv = await facialService.getDetectionsCountByCameraDate({
                cameras: values.cameras,
                companyId: values.companyId,
                startDate: moment(values.period.from).valueOf(),
                endDate: moment(values.period.to).valueOf(),
            });
            if (!(csv instanceof Blob)) {
                throw new Error();
            }
            const url = window.URL.createObjectURL(csv);
            const a = document.createElement('a');
            a.href = url;
            a.download = `facial-count-${moment(new Date()).format('YYYY-MM-DD')}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            onCloseNewReportModal();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal transparent={true} visible={isModalVisible} animationType='fade' onRequestClose={() => onCloseNewReportModal()} >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: 550 }]}>
                    <ScrollView contentContainerStyle={styles.scrollContent}>
                        <MyAppText style={{ fontSize: 22, fontWeight: 'bold', color: theme.colors.labelColor }}>{translate('detectionsReport')}</MyAppText>
                        <CompanyDropdown
                            form={form}
                            fieldName='companyId'
                            required={true}
                            disabled={false}
                            zIndex={4}
                        />
                        <MyCheckbox
                            form={form}
                            fieldName='allCameras'
                            label={translate('includeAllCameras')}
                        />
                        <CamerasDropdown
                            showLabel={true}
                            form={form}
                            fieldName='cameras'
                            required={!form.watch('allCameras')}
                            zIndex={3}
                            companyId={form.getValues().companyId}
                            requiresCompanyId={true}
                        />
                        <MyDateRangePicker
                            label={translate('period')}
                            maxDate={moment().subtract(1, 'day').toDate()}
                            form={form}
                            fieldName='period'
                            zIndex={2}
                            required={true}
                        />
                    </ScrollView>
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => onCloseNewReportModal()}
                        disabled={!form.formState.isValid}
                        confirmText={'download'} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        rowGap: 15,
        height: 660,
        padding: 20
    },
    scrollContent: {
        rowGap: 15,
        padding: 5,
    }
}));
